import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  FormSubmitButton,
  TextFormField,
} from "@/components/ui";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useTrustChecksViewModel } from "@/hooks/matter-view-models/use-trust-checks-view-model";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterTrustCheck,
  useCreateMatterTrustCheckConnection,
  useDeleteMatterTrustCheck,
  useUpdateMatterTrustCheck,
} from "@/lib/data/react-query/matters/matter-trust-check";
import { MatterTrustCheckSchema } from "@/lib/data/schemas/matters/MatterSchema";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type { MatterTrustCheck } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { isNil, omitBy } from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditTrustCheckModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  matterId: string;
  trustCheckId?: string;
  trustCheckType?: "Deposit" | "Withdraw";
  settlementOfferId?: string;
  settlementDemandId?: string;
  settlementCounterId?: string;
  expenseId?: string;
  lienId?: string;
  medicalTreatmentBillId?: string;
  initialData?: Partial<MatterTrustCheck>;
}

const trustCheckFormSchema = MatterTrustCheckSchema.pick({
  amount: true,
  dateReceived: true,
  details: true,
  referenceNumber: true,
}).extend({
  amount: z.preprocess(
    (value) => (value === "" ? null : value),
    MatterTrustCheckSchema.shape.amount,
  ),
});

const TRUST_CHECK_MODAL_FORM_ID = "trust-check-modal-form";

const defaultFormValues = {
  amount: null,
  dateReceived: dayjs().format("YYYY-MM-DD"),
  details: undefined,
  referenceNumber: "",
};

const getTrustCheckFormValues = (
  trustCheck: Record<string, unknown> | undefined,
): z.infer<typeof trustCheckFormSchema> => ({
  ...defaultFormValues,
  amount: (trustCheck?.amount as number | null | undefined) ?? null,
  dateReceived:
    (trustCheck?.dateReceived as string | undefined) ??
    defaultFormValues.dateReceived,
  details: (trustCheck?.details as string | null | undefined) ?? undefined,
  referenceNumber:
    (trustCheck?.referenceNumber as string | null | undefined) ?? "",
});

const getTrustCheckType = (trustCheck: Record<string, unknown> | undefined) =>
  trustCheck?.type as "Deposit" | "Withdraw" | undefined;

const getTitle = (
  editing?: boolean,
  trustCheckType?: "Deposit" | "Withdraw",
) => {
  if (editing) {
    return trustCheckType === "Deposit"
      ? SLIDEOVER_TITLE.TRUST_CHECK_DEPOSIT.UPDATE
      : SLIDEOVER_TITLE.TRUST_CHECK_WITHDRAW.UPDATE;
  } else {
    return trustCheckType === "Deposit"
      ? SLIDEOVER_TITLE.TRUST_CHECK_DEPOSIT.CREATE
      : SLIDEOVER_TITLE.TRUST_CHECK_WITHDRAW.CREATE;
  }
};

const getDescription = (
  editing?: boolean,
  trustCheckType?: "Deposit" | "Withdraw",
) => {
  if (editing) {
    return trustCheckType === "Deposit"
      ? SLIDEOVER_DESCRIPTION.TRUST_CHECK_DEPOSIT.UPDATE
      : SLIDEOVER_DESCRIPTION.TRUST_CHECK_WITHDRAW.UPDATE;
  } else {
    return trustCheckType === "Deposit"
      ? SLIDEOVER_DESCRIPTION.TRUST_CHECK_DEPOSIT.CREATE
      : SLIDEOVER_DESCRIPTION.TRUST_CHECK_WITHDRAW.CREATE;
  }
};

export const AddOrEditTrustCheckModal = ({
  open,
  setOpen,
  editing,
  matterId,
  trustCheckId,
  trustCheckType: initialTrustCheckType,
  settlementOfferId,
  settlementDemandId,
  settlementCounterId,
  expenseId,
  lienId,
  medicalTreatmentBillId,
  initialData,
}: AddOrEditTrustCheckModalProps) => {
  const { updateSlideoverPayload } = useModals();
  const [isDeleting, setIsDeleting] = useState(false);
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const { hasActionPermission } = useUser();
  const hasConnectionTarget = Boolean(
    settlementOfferId ||
      settlementDemandId ||
      settlementCounterId ||
      expenseId ||
      lienId ||
      medicalTreatmentBillId,
  );
  const canCreateTrustCheckConnection = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTrustCheckConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canSubmitTrustCheck = editing
    ? hasActionPermission(
        ACTION_PERMISSION_KEYS.matterTrustCheck,
        ACTION_PERMISSION_VALUES.update,
      )
    : hasActionPermission(
        ACTION_PERMISSION_KEYS.matterTrustCheck,
        ACTION_PERMISSION_VALUES.create,
      ) &&
      (!hasConnectionTarget || canCreateTrustCheckConnection);

  // Fetch matter data
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );

  const matter = matterData.data;
  const trustChecksViewModel = useTrustChecksViewModel(matterId);
  // Get the specific settlement if editing
  const selectedTrustCheck = trustCheckId
    ? trustChecksViewModel.find((trustCheck) => trustCheck.id === trustCheckId)
    : undefined;
  const trustCheckInitialValues = mergeInitialData(
    selectedTrustCheck,
    initialData,
  );
  const trustCheckInitialType = getTrustCheckType(
    trustCheckInitialValues as Record<string, unknown> | undefined,
  );
  const trustCheckType =
    initialTrustCheckType ?? trustCheckInitialType ?? "Deposit";

  const trustCheckForm = useForm<z.infer<typeof trustCheckFormSchema>>({
    resolver: zodResolver(trustCheckFormSchema),
    values:
      editing && trustCheckInitialValues
        ? getTrustCheckFormValues(
            trustCheckInitialValues as Record<string, unknown>,
          )
        : {
            ...defaultFormValues,
          },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const createTrustCheck = useCreateMatterTrustCheck();
  const updateTrustCheck = useUpdateMatterTrustCheck();
  const deleteTrustCheck = useDeleteMatterTrustCheck();
  const createTrustCheckConnection = useCreateMatterTrustCheckConnection();

  const isFormLoading =
    createTrustCheck.isPending ||
    updateTrustCheck.isPending ||
    deleteTrustCheck.isPending ||
    createTrustCheckConnection.isPending;

  const onSubmit = async (data: z.infer<typeof trustCheckFormSchema>) => {
    if (!canSubmitTrustCheck) return;
    let resData;

    if (editing && selectedTrustCheck) {
      const res = await updateTrustCheck.mutateAsync({
        ...data,
        matterId: matter.id,
        dateModified: selectedTrustCheck.dateModified,
        id: selectedTrustCheck.id,
        type: trustCheckType,
      });
      resData = res;
    } else {
      const res = await createTrustCheck.mutateAsync({
        matterId: matter.id,
        ...data,
        type: trustCheckType,
      });

      // Create connections if provided
      if (
        settlementOfferId ||
        settlementDemandId ||
        settlementCounterId ||
        expenseId ||
        lienId ||
        medicalTreatmentBillId
      ) {
        await createTrustCheckConnection.mutateAsync({
          matterId: matter.id,
          trustCheckId: res.data.id,
          settlementOfferId: settlementOfferId ?? undefined,
          settlementDemandId: settlementDemandId ?? undefined,
          settlementCounterId: settlementCounterId ?? undefined,
          expenseId: expenseId ?? undefined,
          lienId: lienId ?? undefined,
          medicalTreatmentBillId: medicalTreatmentBillId ?? undefined,
        });
      }

      resData = res;
      updateSlideoverPayload("matter-trust-check", {
        title:
          trustCheckType === "Deposit"
            ? SLIDEOVER_TITLE.TRUST_CHECK_DEPOSIT.UPDATE
            : SLIDEOVER_TITLE.TRUST_CHECK_WITHDRAW.UPDATE,
        description:
          trustCheckType === "Deposit"
            ? SLIDEOVER_DESCRIPTION.TRUST_CHECK_DEPOSIT.UPDATE
            : SLIDEOVER_DESCRIPTION.TRUST_CHECK_WITHDRAW.UPDATE,
        editing: true,
        trustCheckId: resData.data.id,
      });
    }

    if (closeAfterSave) {
      setOpen(false);
    } else {
      const trustCheck = omitBy(resData.data, isNil);
      trustCheckForm.reset({
        ...defaultFormValues,
        ...getTrustCheckFormValues(trustCheck),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedTrustCheck) return;

    deleteTrustCheck.mutate({
      id: selectedTrustCheck.id,
    });
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      trustCheckForm.reset();
    }
  }, [
    open,
    trustCheckForm,
    trustCheckType,
    selectedTrustCheck,
    updateSlideoverPayload,
  ]);

  return (
    <>
      <Form {...trustCheckForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={getTitle(editing, trustCheckType)}
          description={getDescription(editing, trustCheckType)}
          size="2xl"
          loading={isFormLoading}
          isDirty={trustCheckForm.formState.isDirty}
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {editing &&
                  selectedTrustCheck &&
                  hasActionPermission(
                    ACTION_PERMISSION_KEYS.matterTrustCheck,
                    ACTION_PERMISSION_VALUES.delete,
                  ) && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        setIsDeleting(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={trustCheckForm.formState.isDirty}
                />
                <FormSubmitButton
                  submitFormId={TRUST_CHECK_MODAL_FORM_ID}
                  title={editing ? "Save" : "Add trust check"}
                  loading={isFormLoading}
                  overrideDisabled={
                    !canSubmitTrustCheck || Boolean(initialData)
                  }
                  submitFn={() => {
                    setCloseAfterSave(true);
                  }}
                  disabled={isFormLoading}
                />
              </div>
            </div>
          }
        >
          <form
            id={TRUST_CHECK_MODAL_FORM_ID}
            className="relative flex min-h-0 grow flex-col overflow-visible"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await trustCheckForm.handleSubmit(onSubmit)();
            }}
          >
            <fieldset
              className={cn(
                slideoverFieldsetClassName,
                "my-0 grid-cols-1 px-0 pt-0 pb-6 md:grid-cols-2",
              )}
              disabled={
                editing
                  ? !hasActionPermission(
                      ACTION_PERMISSION_KEYS.matterTrustCheck,
                      ACTION_PERMISSION_VALUES.update,
                    )
                  : false
              }
            >
              <CurrencyFormField
                register={trustCheckForm.register("amount")}
                label="Amount*"
              />
              <DateFormField
                register={trustCheckForm.register("dateReceived")}
                name="dateReceived"
                label="Date received*"
              />
              <TextFormField
                register={trustCheckForm.register("referenceNumber")}
                label="Reference number*"
              />
            </fieldset>
          </form>
        </AppModal>
      </Form>

      {editing && selectedTrustCheck && (
        <ActionConfirmationModal
          open={isDeleting}
          setOpen={setIsDeleting}
          title="Are you sure you want to delete this trust check?"
          description="This action cannot be undone. This will remove the trust check and all associated tasks and files."
          afterActionFn={handleDelete}
        />
      )}

      {/* {providerContactId && (
        <EditContactModal
          activeContactId={providerContactId}
          open={isEditingContact}
          setOpen={setIsEditingContact}
          hasEditPermission={hasActionPermission(
            ACTION_PERMISSION_KEYS.contact,
            ACTION_PERMISSION_VALUES.update,
          )}
        />
      )} */}
    </>
  );
};
